/**
 * The BRAND-COMPOSITION SEAM.
 *
 * Operator ruling: when you switch entire application contexts — personal
 * Career → business DNA — the only thing that changes is the LOGO. The
 * navigation chrome, its typography and its behaviour stay identical.
 *
 * So `<kit-scope-nav>` never hardcodes a mark. It resolves a NAMED composition
 * from this registry and renders it into the mark slot. The brand project owns
 * several compositions of the same »P«; an app that still carries its own
 * letterform (DNA's »Q« until its bespoke mark lands) registers one and names
 * it in the scope model — no fork of the component.
 *
 * ```ts
 * registerScopeMark("dna-q", () => html`<svg viewBox="0 0 32 32">…</svg>`);
 * nav.scope = { mark: "dna-q", app: { label: "DNA" }, … };
 * ```
 */

import { html, type TemplateResult } from "../render/html";
import type { Space } from "../brand/spaces";

/** What a composition is told about the current scope. */
export interface ScopeMarkContext {
  /** The app name (upper lockup line). */
  readonly app: string;
  /** The top scope, or null — compositions express it as the accent. */
  readonly space: Space | null;
  /** The in-app section (lower lockup line), or null. */
  readonly section: string | null;
}

export type ScopeMarkRenderer = (ctx: ScopeMarkContext) => TemplateResult;

/** The default composition: the bare platform »P.«, its dot carrying the space. */
export const DEFAULT_SCOPE_MARK = "platform-p";

/**
 * The bare mark. `aria-hidden` — the nav's own accessible name already says
 * where you are, and a second reading of the brand is noise.
 */
const platformP: ScopeMarkRenderer = ({ space }) =>
  html`<kit-brand-mark
    class="scope-brand"
    space=${space ?? ""}
    aria-hidden="true"
  ></kit-brand-mark>`;

/**
 * The full in-gap lockup — the app name set INSIDE the mark's gap. Reserved for
 * large surfaces (login, splash); in a header the stacked lockup carries the
 * name instead.
 */
const platformPLockup: ScopeMarkRenderer = ({ app, space }) =>
  html`<kit-brand-mark
    class="scope-brand scope-brand-lockup"
    app=${app}
    space=${space ?? ""}
    aria-hidden="true"
  ></kit-brand-mark>`;

/** No mark at all — for embedded surfaces that already show the brand. */
const noMark: ScopeMarkRenderer = () => html``;

const BUILT_IN: ReadonlyArray<readonly [string, ScopeMarkRenderer]> = [
  [DEFAULT_SCOPE_MARK, platformP],
  ["platform-p-lockup", platformPLockup],
  ["none", noMark],
];

const REGISTRY = new Map<string, ScopeMarkRenderer>(BUILT_IN);

/** Thrown when a scope model names a composition nobody registered. */
export class ScopeMarkError extends Error {
  constructor(name: string) {
    super(
      `[web-kit] unknown brand composition "${name}" — register it with ` +
        `registerScopeMark("${name}", …) before naming it in a scope model ` +
        `(known: ${[...REGISTRY.keys()].join(", ")}).`
    );
    this.name = "ScopeMarkError";
  }
}

/**
 * Register (or replace) a named brand composition. Idempotent for the same
 * renderer, so app bootstraps may call it on every hot reload.
 */
export function registerScopeMark(
  name: string,
  render: ScopeMarkRenderer
): void {
  if (name.trim().length === 0) {
    throw new ScopeMarkError("(empty)");
  }
  REGISTRY.set(name, render);
}

/** Resolve a composition by name. `null`/absent → the platform default. */
export function resolveScopeMark(name: string | null | undefined): ScopeMarkRenderer {
  const key = name ?? DEFAULT_SCOPE_MARK;
  const render = REGISTRY.get(key);
  if (!render) throw new ScopeMarkError(key);
  return render;
}

/** The names currently registered — for demos, docs and diagnostics. */
export function scopeMarkNames(): string[] {
  return [...REGISTRY.keys()];
}

/** Test seam: drop app-registered compositions, keep the built-ins. */
export function resetScopeMarksForTesting(): void {
  REGISTRY.clear();
  for (const [name, render] of BUILT_IN) REGISTRY.set(name, render);
}
